import React, { useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Typography, Button, Box, InputAdornment, IconButton, Divider } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
// import TwitterIcon from '@mui/icons-material/Twitter';
// import MicrosoftIcon from '@mui/icons-material/AccountCircle';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import TextField from '../components/TextField';
import { UserContext } from '../context/UserContext';
import CustomLink from '../components/CustomLink';
import { API_BASE_URL } from '../constants';
import { useCheckUser, useLogin } from '../queries/auth';
import InstagramIcon from '@mui/icons-material/Instagram';
import { toast } from 'react-toastify';

const schema = z.object({
  email: z.string().email('Please enter a valid email address.').min(1, 'Email is required'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long.')
    .regex(/[A-Z]/, 'Password must include at least one uppercase letter.')
    .regex(/\d/, 'Password must include at least one number.')
    .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must include at least one special character.'),
});

const Login = () => {
  const { login } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();

  const { mutate: loginMutate, isPending: isLoggingIn } = useLogin();
  const { mutate: checkUser, isPending: isCheckingUser } = useCheckUser();

  const [showErrorMessage, setShowErrorMessage] = React.useState(false);
  const [showPasswordField, setShowPasswordField] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { handleSubmit, setValue, getValues, setError } = methods;

  const syncAutofilledValues = (form) => {
    if (!form?.elements) {
      return;
    }

    ['email', 'password'].forEach((fieldName) => {
      const field = form.elements.namedItem(fieldName);
      const nativeValue = typeof field?.value === 'string' ? field.value.trim() : '';
      if (nativeValue && nativeValue !== getValues(fieldName)) {
        setValue(fieldName, nativeValue, { shouldDirty: true, shouldValidate: true });
      }
    });
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const error = query.get('error');

    if (error) {
      toast(decodeURIComponent(error), { type: 'error' });
      navigate('/login');
    }
  }, [location, navigate]);

  const handleNext = (emailValue) => {
    const email = (emailValue || '').trim();
    if (!email) {
      setError('email', { type: 'manual', message: 'Please enter a valid email address.' });
      return;
    }

    checkUser(
      { data: { email } },
      {
        onSuccess: () => {
          setShowPasswordField(true);
          setShowErrorMessage(false);
        },
        onError: () => {
          setShowPasswordField(false);
          setShowErrorMessage(true);
        },
      },
    );
  };

  const handleLogin = async (data) => {
    const { email, password } = data;

    loginMutate(
      { data: { email, password } },
      {
        onSuccess: ({ data }) => {
          if (data.token) {
            sessionStorage.removeItem('leadAgentNoticeAccepted');
            localStorage.setItem('authToken', data.token);
          } else {
            toast('Failed to login.', { type: 'error' });
            return;
          }

          if (data.user) {
            login(data.user);
            toast('Logged in successfully!', { type: 'success' });
          } else {
            toast('Failed to login.', { type: 'error' });
          }
        },
        onError: (error) => {
          const message = error.response?.data?.message || error.message;
          toast(message, { type: 'error' });
        },
      },
    );
  };

  const handleSocialLogin = (provider) => {
    window.location.href = `${API_BASE_URL}/auth/${provider}`;
  };

  return (
    <Box height='100%'>
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        minHeight='100%'
        width='100%'
        padding={{ xs: 1.5, sm: 2 }}
        paddingY={{ xs: 3, sm: 4 }}
      >
        {showErrorMessage && (
          <Typography
            variant='body1'
            color='error'
            align='center'
            sx={{
              marginBottom: { xs: '12px', sm: '16px' },
              fontSize: { xs: '14px', sm: '16px' },
            }}
          >
            We couldn't find your account. Please{' '}
            <Button
              color='primary'
              component={CustomLink}
              to='/register'
              sx={{
                textTransform: 'none',
                fontWeight: 'normal',
                padding: '0',
                fontSize: { xs: '14px', sm: '16px' },
              }}
            >
              SIGN UP
            </Button>{' '}
            to get started!
          </Typography>
        )}

        <Typography
          variant='h4'
          gutterBottom
          sx={{
            fontSize: { xs: '24px', sm: '32px', md: '40px' },
            textAlign: 'center',
          }}
        >
          Log in to your account
        </Typography>
        <Typography
          variant='body1'
          color='textSecondary'
          gutterBottom
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            textAlign: 'center',
            mb: { xs: 2, sm: 3 },
          }}
        >
          Don't have an account?{' '}
          <Button
            color='primary'
            component={CustomLink}
            to='/register'
            sx={{
              textTransform: 'none',
              fontWeight: 'normal',
              fontSize: { xs: '14px', sm: '16px' },
            }}
          >
            Sign Up
          </Button>
        </Typography>

        <Box width='100%' maxWidth={400} mt={{ xs: 2, sm: 3 }}>
          <Button
            variant='outlined'
            fullWidth
            startIcon={<GoogleIcon />}
            sx={{
              marginBottom: { xs: '8px', sm: '10px' },
              backgroundColor: '#fff',
              color: '#000',
              textTransform: 'none',
              borderColor: '#ddd',
              fontSize: { xs: '14px', sm: '16px' },
              py: { xs: 1, sm: 1.5 },
            }}
            onClick={() => handleSocialLogin('google')}
          >
            Google
          </Button>
          <Button
            variant='outlined'
            fullWidth
            startIcon={<FacebookIcon />}
            sx={{
              marginBottom: { xs: '8px', sm: '10px' },
              backgroundColor: '#fff',
              color: '#3b5998',
              textTransform: 'none',
              borderColor: '#ddd',
              fontSize: { xs: '14px', sm: '16px' },
              py: { xs: 1, sm: 1.5 },
            }}
            onClick={() => handleSocialLogin('facebook')}
          >
            Facebook / <InstagramIcon sx={{ marginRight: '6px', marginLeft: '6px' }} /> Instagram
          </Button>
        </Box>

        <Divider
          sx={{
            width: '100%',
            maxWidth: 400,
            margin: { xs: '16px 0', sm: '20px 0' },
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='body2'
            color='textSecondary'
            sx={{
              fontSize: { xs: '14px', sm: '16px' },
              backgroundColor: '#f4f4f4',
              padding: '0 8px',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              whiteSpace: 'nowrap',
            }}
          >
            Or with email and password
          </Typography>
        </Divider>

        <Box width='100%' maxWidth={400} display='flex' flexDirection='column' alignItems='center'>
          <FormProvider {...methods}>
            <form
              noValidate
              onSubmit={(event) => {
                syncAutofilledValues(event.currentTarget);
                if (showPasswordField) {
                  return handleSubmit(handleLogin)(event);
                }

                event.preventDefault();
                const emailField = event.currentTarget.elements.namedItem('email');
                const emailValue =
                  getValues('email') || (typeof emailField?.value === 'string' ? emailField.value : '');
                handleNext(emailValue);
              }}
              style={{ width: '100%' }}
            >
              <TextField
                label='Email Address'
                name='email'
                placeholder='Enter your email address'
                autoComplete='username'
                slotProps={{
                  htmlInput: {
                    autoComplete: 'username',
                    readOnly: showPasswordField,
                  },
                }}
                sx={{ mb: { xs: 1.5, sm: 2 } }}
              />
              {showPasswordField && (
                <TextField
                  label='Password'
                  name='password'
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='current-password'
                  slotProps={{
                    htmlInput: { autoComplete: 'current-password' },
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={() => setShowPassword(!showPassword)}
                            edge='end'
                            size='small'
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ mb: { xs: 1.5, sm: 2 } }}
                />
              )}
              <Button
                type='submit'
                variant='contained'
                color='primary'
                fullWidth
                sx={{
                  marginTop: { xs: '8px', sm: '10px' },
                  py: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '14px', sm: '16px' },
                }}
                disabled={isLoggingIn || isCheckingUser}
              >
                {showPasswordField ? 'Login' : 'Next'}
              </Button>
            </form>
          </FormProvider>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
